// Sound Map — a hand-authored phonetics cheat sheet, styled like a note jotted on
// paper. Every letter/digraph → plain-English sound → IPA → an example you can
// hear. Hand-authored on purpose: sound mapping is exactly the thing an AI image
// generator gets wrong.
import { speakButtonHTML } from "../lib/audio.js";

const GROUPS = [
  {
    title: "Oral vowels — open & closed",
    note: "Portuguese vowels shift with stress — a, e and o each have an open and a closed version.",
    color: "text-emerald-700",
    rows: [
      ["a", "“ah” as in father", "/a/", "casa"],
      ["e", "“eh” (open) or “ay” (closed)", "/ɛ/ /e/", "pé"],
      ["i", "“ee” as in see", "/i/", "vi"],
      ["o", "“aw” (open) or “oh” (closed)", "/ɔ/ /o/", "avó"],
      ["u", "“oo” as in food", "/u/", "luz"],
    ],
  },
  {
    title: "Consonants unlike English",
    note: "Where beginners slip — the rest sound close to English.",
    color: "text-amber-700",
    rows: [
      ["c + e/i", "soft “s”", "/s/", "cidade"],
      ["ç", "always soft “s”", "/s/", "almoço"],
      ["g + e/i", "“zh”, like the s in measure", "/ʒ/", "gente"],
      ["j", "“zh”, like the s in measure", "/ʒ/", "já"],
      ["r (start) / rr", "guttural, in the throat", "/ʁ/", "rua"],
      ["r (between vowels)", "a quick tap", "/ɾ/", "caro"],
      ["s (between vowels)", "“z”", "/z/", "casa"],
      ["s (final)", "“sh”", "/ʃ/", "lápis"],
      ["x", "often “sh”", "/ʃ/", "peixe"],
    ],
  },
  {
    title: "Digraphs & nasal letters",
    note: "Two letters — or a til (~) — making one sound of their own; the til marks a nasal vowel.",
    color: "text-sky-700",
    rows: [
      ["ch", "“sh” as in shoe", "/ʃ/", "chave"],
      ["lh", "“lli” as in million", "/ʎ/", "filho"],
      ["nh", "“ny” as in canyon", "/ɲ/", "vinho"],
      ["rr", "guttural, in the throat", "/ʁ/", "carro"],
      ["ss", "sharp “s” between vowels", "/s/", "passo"],
      ["ão / ã", "nasal “ow”, through the nose", "/ɐ̃w̃/ /ɐ̃/", "pão"],
    ],
  },
];

function groupHTML(g) {
  const rows = g.rows
    .map(
      ([letter, sound, ipa, ex]) => `
      <div class="flex items-baseline gap-3 py-1.5 border-b border-dashed border-amber-300/50 last:border-0">
        <span class="font-bold ${g.color} text-xl min-w-[3.4rem]">${letter}</span>
        <span class="flex-1 text-sage-700">${sound}</span>
        <span class="text-xs text-sage-400 whitespace-nowrap" style="font-family:ui-monospace,monospace">${ipa}</span>
        <span class="font-semibold text-sage-800 min-w-[4.5rem] text-right">${ex}</span>${speakButtonHTML(ex)}
      </div>`
    )
    .join("");
  return `
    <div class="mb-6 last:mb-0">
      <h3 class="text-2xl ${g.color} sketch-underline inline-block mb-1">${g.title}</h3>
      <p class="text-sm text-sage-500 mb-2" style="font-family:Inter,sans-serif">${g.note}</p>
      <div>${rows}</div>
    </div>`;
}

export function soundMapView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <div class="flex items-center gap-2 mb-1">
        <h2 class="text-4xl font-script text-sage-800">Sound Map</h2><span class="text-2xl">🔊</span>
      </div>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Portuguese spelling is fairly regular once you know the rules — especially the soft letters, the nasals, and the open/closed vowels. Tap 🔊 to hear each example.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        ${GROUPS.map(groupHTML).join("")}
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">European Portuguese loves the “sh” sound: a final <b>s</b> and the letter <b>x</b> often sound like “sh”. Unstressed vowels are also reduced, so an unstressed <i>e</i> can almost vanish.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
